import React, { useMemo } from 'react';
import { Stack, Text, Group, RingProgress } from '@mantine/core';
import { useDashboardData } from '../../../../hooks/dashboard/useDashboardData';
import { useProductivityMetrics } from '../../../../hooks/dashboard/useProductivityMetrics';

interface DashboardStatsProps {
  className?: string;
}

export const DashboardStats: React.FC<DashboardStatsProps> = ({ className }) => {
  const dashboardData = useDashboardData();
  const pm = useProductivityMetrics();

  // Calculate clean, meaningful stats with null safety
  const stats = useMemo(() => {
    const completionRate = dashboardData.totalTasks > 0
      ? Math.round((dashboardData.completedTasks / dashboardData.totalTasks) * 100)
      : 0;
    const thisWeekCompletions = pm.weeklyCompletions.reduce((a,b)=>a+b,0);
    const avgDailyCompletions = Math.round(thisWeekCompletions / 7);
    return {
      completionRate,
      avgDailyCompletions,
      currentStreak: pm.currentCompletionStreak || 0,
      productivity: {
        score: pm.productivityScore,
        level: pm.productivityScore >= 85 ? 'Excellent' :
               pm.productivityScore >= 70 ? 'Good' :
               pm.productivityScore >= 50 ? 'Fair' : 'Building'
      }
    };
  }, [dashboardData, pm]);

  const getScoreColor = (score: number) => {
    if (score >= 85) return 'green';
    if (score >= 70) return 'blue';
    if (score >= 50) return 'yellow';
    return 'orange';
  };

  // Don't render if we don't have valid dashboard data
  if (!dashboardData || dashboardData.totalTasks === 0) {
    return null;
  }

  return (
    <Stack gap="md" className={className}>
        <Group justify="center" gap="lg">
          <RingProgress
            size={80}
            thickness={6}
            roundCaps
            sections={[
              { value: stats.completionRate, color: getScoreColor(stats.productivity.score) }
            ]}
            label={
              <Stack gap={0} align="center">
                <Text size="md" fw={700} ta="center">
                  {stats.completionRate}%
                </Text>
                <Text size="xs" c="dimmed" ta="center">
                  done
                </Text>
              </Stack>
            }
          />
          
          <Stack gap="xs" style={{ flex: 1 }}>
            <Group justify="space-between">
              <Text size="xs" c="dimmed">Level</Text>
              <Text size="sm" fw={500} c={getScoreColor(stats.productivity.score)}>
                {stats.productivity.level}
              </Text>
            </Group>

            <Group justify="space-between">
              <Text size="xs" c="dimmed">Streak</Text>
              <Text size="sm" fw={500}>{stats.currentStreak} days</Text>
            </Group>

            <Group justify="space-between">
              <Text size="xs" c="dimmed">Daily Avg</Text>
              <Text size="sm" fw={500}>{stats.avgDailyCompletions}</Text>
            </Group>
          </Stack>
        </Group>
    <Text size="xs" c="dimmed" ta="center">
          {stats.productivity.score >= 85 ? '🎉 Excellent productivity!' :
            stats.productivity.score >= 70 ? '💪 Great momentum!' :
            stats.currentStreak > 0 ? `🔥 ${stats.currentStreak}-day streak` : '🚀 Keep going'}
        </Text>
  </Stack>
  );
};